/**
 * Voice Activity Detection (VAD) integration.
 *
 * Uses @ricky0123/vad-web for browser-side speech detection.
 * Provides start/stop/pause lifecycle, threshold management,
 * and adaptive sensitivity mode.
 */

import { MicVAD } from '@ricky0123/vad-web';
import type { RealTimeVADOptions } from '@ricky0123/vad-web';
import {
  VAD_DEFAULT_POSITIVE_THRESHOLD,
  VAD_DEFAULT_NEGATIVE_THRESHOLD,
  STORAGE_KEYS,
} from '../utils/constants';

// ── Asset Paths ──
// VAD model and worklet files are copied to public/vad/ at build time.
// ONNX WASM runtime is loaded from CDN for reliability and caching.
const VAD_BASE_ASSET_PATH = import.meta.env.BASE_URL + 'vad/';
const ONNX_WASM_BASE_PATH = 'https://cdn.jsdelivr.net/npm/onnxruntime-web@1.22.0/dist/';

// ── Adaptive VAD Constants ──
const VAD_MIN_THRESHOLD = 0.5;
const VAD_MAX_THRESHOLD = 0.95;
const VAD_ADAPTIVE_STEP_UP = 0.05;
const VAD_ADAPTIVE_STEP_DOWN = 0.02;
const VAD_MISFIRE_WINDOW_MS = 30_000;
const VAD_MISFIRE_TRIGGER_COUNT = 5;
const VAD_RELAX_INTERVAL_MS = 30_000;

/** VAD event callbacks */
export interface VADCallbacks {
  /** Called when speech starts */
  onSpeechStart: () => void;
  /** Called when speech ends — provides the audio data (Float32Array, 16kHz) */
  onSpeechEnd: (audio: Float32Array) => void;
  /** Called when VAD detects a misfire (short spurious detection) */
  onMisfire?: () => void;
}

/** VAD configuration */
export interface VADConfig {
  positiveThreshold?: number;
  negativeThreshold?: number;
  /** Minimum speech duration in ms to avoid misfires */
  minSpeechMs?: number;
  /** Pre-speech padding in ms */
  preSpeechPadMs?: number;
  /** Whether to start listening immediately after creation */
  startOnLoad?: boolean;
}

/**
 * Wrapper around MicVAD providing lifecycle, adaptive sensitivity,
 * and clean start/stop/destroy semantics.
 */
export class VADManager {
  private vad: MicVAD | null = null;
  private callbacks: VADCallbacks;
  private config: VADConfig;
  private _isActive = false;

  // Adaptive mode state
  private adaptiveEnabled: boolean;
  private effectiveThreshold: number;
  private misfireLog: number[] = [];
  private relaxTimer: ReturnType<typeof setInterval> | null = null;

  constructor(callbacks: VADCallbacks, config?: VADConfig) {
    this.callbacks = callbacks;
    this.config = config ?? {};

    // Load persisted settings
    this.effectiveThreshold = parseFloat(
      localStorage.getItem(STORAGE_KEYS.VAD_THRESHOLD) ??
        String(config?.positiveThreshold ?? VAD_DEFAULT_POSITIVE_THRESHOLD),
    );
    this.adaptiveEnabled =
      localStorage.getItem(STORAGE_KEYS.VAD_ADAPTIVE) !== 'false';
  }

  /** Whether the VAD is actively listening */
  get isActive(): boolean {
    return this._isActive;
  }

  /** Current effective positive threshold */
  get threshold(): number {
    return this.effectiveThreshold;
  }

  /** Whether adaptive mode is enabled */
  get isAdaptive(): boolean {
    return this.adaptiveEnabled;
  }

  /**
   * Initialize and start the VAD.
   * Must be called from a user gesture context (first time).
   */
  async init(): Promise<void> {
    if (this.vad) return;

    const posThreshold = this.effectiveThreshold;
    const negThreshold = computeNegativeThreshold(posThreshold);

    const vadOptions: Partial<RealTimeVADOptions> = {
      positiveSpeechThreshold: posThreshold,
      negativeSpeechThreshold: negThreshold,
      minSpeechMs: this.config.minSpeechMs ?? 300,
      preSpeechPadMs: this.config.preSpeechPadMs ?? 300,
      startOnLoad: this.config.startOnLoad ?? false,
      baseAssetPath: VAD_BASE_ASSET_PATH,
      onnxWASMBasePath: ONNX_WASM_BASE_PATH,
      onSpeechStart: () => {
        this.callbacks.onSpeechStart();
      },
      onSpeechEnd: (audio: Float32Array) => {
        this.recordRealSpeech();
        this.callbacks.onSpeechEnd(audio);
      },
      onVADMisfire: () => {
        this.recordMisfire();
        this.callbacks.onMisfire?.();
      },
    };

    this.vad = await MicVAD.new(vadOptions);
  }

  /** Start listening (resume VAD) */
  async start(): Promise<void> {
    if (!this.vad) {
      await this.init();
    }
    await this.vad!.start();
    this._isActive = true;
    this.startRelaxTimer();
  }

  /** Pause listening (keep resources allocated) */
  async stop(): Promise<void> {
    if (this.vad) {
      await this.vad.pause();
    }
    this._isActive = false;
  }

  /** Fully destroy the VAD instance and release resources */
  async destroy(): Promise<void> {
    this._isActive = false;
    this.stopRelaxTimer();
    if (this.vad) {
      await this.vad.pause();
      await this.vad.destroy();
      this.vad = null;
    }
  }

  /** Update sensitivity threshold at runtime */
  setSensitivity(positiveThreshold: number): void {
    this.effectiveThreshold = Math.max(
      VAD_MIN_THRESHOLD,
      Math.min(VAD_MAX_THRESHOLD, positiveThreshold),
    );
    this.applyThreshold();

    if (!this.adaptiveEnabled) {
      localStorage.setItem(
        STORAGE_KEYS.VAD_THRESHOLD,
        String(this.effectiveThreshold),
      );
    }
  }

  /** Toggle adaptive mode on/off */
  setAdaptive(enabled: boolean): void {
    this.adaptiveEnabled = enabled;
    localStorage.setItem(STORAGE_KEYS.VAD_ADAPTIVE, String(enabled));

    if (!enabled) {
      localStorage.setItem(
        STORAGE_KEYS.VAD_THRESHOLD,
        String(this.effectiveThreshold),
      );
      this.misfireLog = [];
    }
  }

  // ── Adaptive Logic ──

  private recordMisfire(): void {
    if (!this.adaptiveEnabled) return;

    const now = Date.now();
    this.misfireLog.push(now);
    this.misfireLog = this.misfireLog.filter(
      (t) => now - t < VAD_MISFIRE_WINDOW_MS,
    );

    if (this.misfireLog.length >= VAD_MISFIRE_TRIGGER_COUNT) {
      const newThreshold = Math.min(
        VAD_MAX_THRESHOLD,
        this.effectiveThreshold + VAD_ADAPTIVE_STEP_UP,
      );
      if (newThreshold !== this.effectiveThreshold) {
        this.effectiveThreshold = parseFloat(newThreshold.toFixed(2));
        this.applyThreshold();
      }
      this.misfireLog = [];
    }
  }

  private recordRealSpeech(): void {
    this.misfireLog = [];
  }

  private startRelaxTimer(): void {
    if (this.relaxTimer) return;
    this.relaxTimer = setInterval(() => {
      if (!this.adaptiveEnabled) return;
      if (this.effectiveThreshold <= VAD_DEFAULT_POSITIVE_THRESHOLD) return;

      const now = Date.now();
      const lastMisfire =
        this.misfireLog.length > 0
          ? this.misfireLog[this.misfireLog.length - 1]!
          : 0;
      if (now - lastMisfire >= VAD_RELAX_INTERVAL_MS) {
        const newThreshold = Math.max(
          VAD_DEFAULT_POSITIVE_THRESHOLD,
          this.effectiveThreshold - VAD_ADAPTIVE_STEP_DOWN,
        );
        if (newThreshold !== this.effectiveThreshold) {
          this.effectiveThreshold = parseFloat(newThreshold.toFixed(2));
          this.applyThreshold();
        }
      }
    }, VAD_RELAX_INTERVAL_MS);
  }

  private stopRelaxTimer(): void {
    if (this.relaxTimer) {
      clearInterval(this.relaxTimer);
      this.relaxTimer = null;
    }
  }

  private applyThreshold(): void {
    if (this.vad) {
      const pos = this.effectiveThreshold;
      const neg = computeNegativeThreshold(pos);
      this.vad.setOptions({
        positiveSpeechThreshold: pos,
        negativeSpeechThreshold: neg,
      });
    }
  }
}

// ── Utility Functions ──

/**
 * Compute negative threshold from positive (37.5% ratio, matching
 * the default 0.8→0.3 relationship).
 */
function computeNegativeThreshold(positive: number): number {
  return parseFloat((positive * 0.375).toFixed(4));
}

/** Update VAD thresholds at runtime (without recreating the instance) */
export function updateVADThresholds(
  vad: MicVAD,
  positiveThreshold: number,
): void {
  const negativeThreshold = computeNegativeThreshold(positiveThreshold);
  vad.setOptions({
    positiveSpeechThreshold: positiveThreshold,
    negativeSpeechThreshold: negativeThreshold,
  });
}

/** Get default VAD thresholds */
export function getDefaultThresholds(): {
  positive: number;
  negative: number;
} {
  return {
    positive: VAD_DEFAULT_POSITIVE_THRESHOLD,
    negative: VAD_DEFAULT_NEGATIVE_THRESHOLD,
  };
}
